import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoleBadge from '../components/ui/RoleBadge';

const { FiUsers, FiTarget, FiRefreshCw, FiCheck, FiX, FiEdit, FiTrash2, FiPlus, FiBriefcase, FiSettings, FiSave } = FiIcons;

const AdminDashboard = () => {
  const { 
    profiles, 
    matches, 
    companies,
    formContent,
    updateProfile, 
    deleteProfile, 
    createMatch, 
    recomputeMatches,
    addCompany,
    removeCompany,
    updateFormContent
  } = useData();
  
  const [activeTab, setActiveTab] = useState('profiles');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editProfile, setEditProfile] = useState(null);
  const [showCreateMatch, setShowCreateMatch] = useState(false);
  const [showCompanyManager, setShowCompanyManager] = useState(false);
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [editingFormContent, setEditingFormContent] = useState(formContent);

  const pendingProfiles = profiles.filter(p => !p.approved);
  const approvedProfiles = profiles.filter(p => p.approved);

  const handleApproveProfile = (profileId) => {
    updateProfile(profileId, { approved: true });
  };

  const handleRejectProfile = (profileId) => {
    deleteProfile(profileId);
  };

  const handleEditProfile = (profile) => {
    setEditProfile({ ...profile });
    setSelectedProfile(profile);
  };

  const handleSaveProfile = () => {
    if (editProfile) {
      updateProfile(editProfile.id, editProfile);
      setEditProfile(null);
      setSelectedProfile(null);
    }
  };

  const handleRecomputeMatches = () => {
    recomputeMatches();
    alert('Matches recomputed successfully!');
  };

  const handleAddCompany = () => {
    if (newCompany.trim()) {
      addCompany(newCompany.trim());
      setNewCompany('');
      alert('Company added successfully!');
    }
  };

  const handleRemoveCompany = (companyName) => {
    if (confirm(`Are you sure you want to remove "${companyName}"?`)) {
      removeCompany(companyName);
    }
  };

  const handleSaveFormContent = () => {
    updateFormContent(editingFormContent);
    setShowFormEditor(false);
    alert('Form content updated successfully!');
  };

  const handleFormContentChange = (field, value) => {
    setEditingFormContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFormOption = (field, option) => {
    if (option.trim()) {
      const currentArray = editingFormContent[field] || [];
      const newArray = [...currentArray, option.trim()];
      handleFormContentChange(field, newArray);
    }
  };

  const handleRemoveFormOption = (field, index) => {
    const currentArray = editingFormContent[field] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    handleFormContentChange(field, newArray);
  };

  const CreateMatchModal = () => {
    const [profile1Id, setProfile1Id] = useState('');
    const [profile2Id, setProfile2Id] = useState('');

    const handleCreateMatch = () => {
      if (profile1Id && profile2Id && profile1Id !== profile2Id) {
        createMatch(profile1Id, profile2Id, 1.0, true);
        setShowCreateMatch(false);
        setProfile1Id('');
        setProfile2Id('');
        alert('Manual match created successfully!');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Manual Match</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile 1
              </label>
              <select
                value={profile1Id}
                onChange={(e) => setProfile1Id(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select a profile</option>
                {approvedProfiles.map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name} ({profile.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile 2
              </label>
              <select
                value={profile2Id}
                onChange={(e) => setProfile2Id(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select a profile</option>
                {approvedProfiles.filter(p => p.id !== profile1Id).map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name} ({profile.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleCreateMatch}
              disabled={!profile1Id || !profile2Id}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              Create Match
            </button>
            <button
              onClick={() => setShowCreateMatch(false)}
              className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CompanyManagerModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Manage Companies</h3>
            <button
              onClick={() => setShowCompanyManager(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          {/* Add Company */}
          <div className="mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                placeholder="Add new company..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCompany()}
              />
              <button
                onClick={handleAddCompany}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add</span>
              </button>
            </div>
          </div>

          {/* Company List */}
          <div className="overflow-y-auto max-h-96">
            <div className="space-y-2">
              {companies.map((company, index) => (
                <div key={company} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiBriefcase} className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{company}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveCompany(company)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove company"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 text-sm text-gray-600">
            Total companies: {companies.length}
          </div>
        </div>
      </div>
    );
  };

  const FormEditorModal = () => {
    const [newOption, setNewOption] = useState('');
    const [selectedField, setSelectedField] = useState('roles');

    const fieldLabels = {
      roles: 'Roles',
      businessTypes: 'Business Types',
      platforms: 'Platforms',
      genres: 'Genres',
      markets: 'Markets',
      needsOffers: 'Needs & Offers',
      experienceLevels: 'Experience Levels',
      companySizes: 'Company Sizes',
      projectStages: 'Project Stages',
      budgetRanges: 'Budget Ranges'
    };

    const handleAddOption = () => {
      if (newOption.trim()) {
        handleAddFormOption(selectedField, newOption);
        setNewOption('');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Edit Form Content</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSaveFormContent}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
              <button
                onClick={() => setShowFormEditor(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Field Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Field to Edit
              </label>
              <div className="space-y-2">
                {Object.keys(fieldLabels).map(field => (
                  <button
                    key={field}
                    onClick={() => setSelectedField(field)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                      selectedField === field
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {fieldLabels[field]}
                  </button>
                ))}
              </div>
            </div>

            {/* Options Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {fieldLabels[selectedField]} Options
              </label>
              
              {/* Add New Option */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder={`Add new ${fieldLabels[selectedField].toLowerCase()}...`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                />
                <button
                  onClick={handleAddOption}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                </button>
              </div>

              {/* Current Options */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {editingFormContent[selectedField]?.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-900">{option}</span>
                    <button
                      onClick={() => handleRemoveFormOption(selectedField, index)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove option"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-sm text-gray-600">
                Total options: {editingFormContent[selectedField]?.length || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProfileEditModal = () => {
    if (!editProfile) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSaveProfile}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>Save</span>
              </button>
              <button
                onClick={() => {
                  setEditProfile(null);
                  setSelectedProfile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={editProfile.name || ''}
                onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={editProfile.company || ''}
                onChange={(e) => setEditProfile({ ...editProfile, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={editProfile.role || ''}
                onChange={(e) => setEditProfile({ ...editProfile, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select role</option>
                {formContent.roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
              <select
                value={editProfile.businessType || ''}
                onChange={(e) => setEditProfile({ ...editProfile, businessType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select business type</option>
                {formContent.businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                type="url"
                value={editProfile.website || ''}
                onChange={(e) => setEditProfile({ ...editProfile, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={editProfile.email || ''}
                onChange={(e) => setEditProfile({ ...editProfile, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
              <textarea
                value={editProfile.summary || ''}
                onChange={(e) => setEditProfile({ ...editProfile, summary: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="approved"
                checked={editProfile.approved || false}
                onChange={(e) => setEditProfile({ ...editProfile, approved: e.target.checked })}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="approved" className="ml-2 text-sm text-gray-700">Approved</label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage profiles, matches, companies, and platform settings.</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Profiles</p>
                  <p className="text-2xl font-bold text-gray-900">{profiles.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Approved</p>
                  <p className="text-2xl font-bold text-gray-900">{approvedProfiles.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{pendingProfiles.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiTarget} className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Matches</p>
                  <p className="text-2xl font-bold text-gray-900">{matches.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiBriefcase} className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Companies</p>
                  <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-4 mb-8">
            <button
              onClick={handleRecomputeMatches}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              <span>Recompute Matches</span>
            </button>

            <button
              onClick={() => setShowCreateMatch(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Create Manual Match</span>
            </button>

            <button
              onClick={() => setShowCompanyManager(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiBriefcase} className="w-4 h-4" />
              <span>Manage Companies</span>
            </button>

            <button
              onClick={() => setShowFormEditor(true)}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiSettings} className="w-4 h-4" />
              <span>Edit Form Content</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
            <button
              onClick={() => setActiveTab('profiles')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'profiles'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Profiles ({profiles.length})
            </button>
            <button
              onClick={() => setActiveTab('matches')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'matches'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Matches ({matches.length})
            </button>
          </div>

          {/* Content */}
          {activeTab === 'profiles' && (
            <div className="space-y-6">
              {/* Pending Profiles */}
              {pendingProfiles.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Pending Approval ({pendingProfiles.length})
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {pendingProfiles.map(profile => (
                      <div key={profile.id} className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-400">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{profile.name}</h3>
                            {profile.company && (
                              <p className="text-sm text-gray-600">{profile.company}</p>
                            )}
                            <RoleBadge role={profile.role} />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditProfile(profile)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                              title="Edit Profile"
                            >
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleApproveProfile(profile.id)}
                              className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                              title="Approve"
                            >
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectProfile(profile.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                              title="Reject"
                            >
                              <SafeIcon icon={FiX} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {profile.summary && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{profile.summary}</p>
                        )}
                        <div className="text-xs text-gray-500">
                          Created: {new Date(profile.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Profiles */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Approved Profiles ({approvedProfiles.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {approvedProfiles.map(profile => (
                    <div key={profile.id} className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{profile.name}</h3>
                          {profile.company && (
                            <p className="text-sm text-gray-600">{profile.company}</p>
                          )}
                          <RoleBadge role={profile.role} />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditProfile(profile)}
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Edit Profile"
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteProfile(profile.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {profile.summary && (
                        <p className="text-sm text-gray-600 line-clamp-2">{profile.summary}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'matches' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                All Matches ({matches.length})
              </h2>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profile 1
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profile 2
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {matches.map(match => {
                        const profile1 = profiles.find(p => p.id === match.profile1Id);
                        const profile2 = profiles.find(p => p.id === match.profile2Id);
                        
                        return (
                          <tr key={match.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {profile1?.name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500">{profile1?.role}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {profile2?.name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500">{profile2?.role}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">
                                {Math.round(match.score * 100)}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                match.isManual 
                                  ? 'bg-purple-100 text-purple-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {match.isManual ? 'Manual' : 'Auto'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(match.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateMatch && <CreateMatchModal />}
      {showCompanyManager && <CompanyManagerModal />}
      {showFormEditor && <FormEditorModal />}
      {editProfile && <ProfileEditModal />}
    </>
  );
};

export default AdminDashboard;